import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Flex, Input, Modal } from 'antd';
import { TbCategory2, TbPlus, TbSettings, TbTrash } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { taskService } from '@/services';
import {
    DragDropContext,
    Draggable,
    DraggableStyle,
    DragHandle,
    Droppable,
    DropResult,
} from '@/shared/components/drag-and-drop';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DropdownButtonSelect, {
    DropdownButtonOption,
} from '@/shared/components/dropdowns/DropdownButton/DropdownButtonSelect';
import { DropdownMenuLabel } from '@/shared/components/dropdowns/DropdownPanel';
import { PlatformColorPicker } from '@/shared/components/forms';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { TaskStatus, TaskStatusCategory, TaskStatusSystemKey } from '@/types';

import TaskStatusIcon from '../TaskStatusIcon';

interface ManageTaskStatusesModalProps {
    onChanged: () => void;
    onClose: () => void;
    open: boolean;
}

interface StatusDraft {
    category: TaskStatusCategory;
    clientId: string;
    color: string;
    id?: string;
    isDefault: boolean;
    name: string;
    systemKey?: TaskStatusSystemKey | null;
}

const categoryDescriptions: Record<TaskStatusCategory, string> = {
    [TaskStatusCategory.ACTIVE]: 'Open work that remains actionable and in progress.',
    [TaskStatusCategory.COMPLETED]: 'Work that has been successfully finished.',
    [TaskStatusCategory.CANCELED]: 'Work that was closed without being completed.',
};

const toDraft = (status: TaskStatus): StatusDraft => ({
    category: status.category,
    clientId: status.id,
    color: status.color,
    id: status.id,
    isDefault: status.isDefault,
    name: status.name,
    systemKey: status.systemKey,
});

const ManageTaskStatusesModal: FC<ManageTaskStatusesModalProps> = ({ onChanged, onClose, open }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statuses, setStatuses] = useState<StatusDraft[]>([]);
    const categoryColors: Record<TaskStatusCategory, string> = {
        [TaskStatusCategory.ACTIVE]: token.colorPrimary,
        [TaskStatusCategory.COMPLETED]: token.colorSuccess,
        [TaskStatusCategory.CANCELED]: token.colorError,
    };
    const categoryOptions: Array<DropdownButtonOption<TaskStatusCategory>> = Object.values(TaskStatusCategory).map(
        (category) => ({
            description: categoryDescriptions[category],
            icon: <TaskStatusIcon status={{ category, color: categoryColors[category] }} />,
            label: category,
            value: category,
        }),
    );
    const iconButtonStyle = {
        alignItems: 'center',
        display: 'inline-flex',
        height: '2rem',
        justifyContent: 'center',
        minWidth: '2rem',
        width: '2rem',
    };

    useEffect(() => {
        if (!open) {
            return;
        }

        const load = async () => {
            try {
                setLoading(true);
                setStatuses((await taskService.statuses.list()).map(toDraft));
            } catch (error) {
                captureException(error);
                toast.error('Unable to Load Task Statuses');
            } finally {
                setLoading(false);
            }
        };

        void load();
    }, [open]);

    const updateDraft = (clientId: string, updates: Partial<StatusDraft>) => {
        setStatuses((current) =>
            current.map((status) => (status.clientId === clientId ? { ...status, ...updates } : status)),
        );
    };
    const removeDraft = (draft: StatusDraft) => {
        if (draft.isDefault) {
            return;
        }
        setStatuses((current) => current.filter((status) => status.clientId !== draft.clientId));
    };
    const addStatus = () =>
        setStatuses((current) => [
            ...current,
            {
                category: TaskStatusCategory.ACTIVE,
                clientId: crypto.randomUUID(),
                color: token.colorPrimary,
                isDefault: false,
                name: 'New Status',
            },
        ]);
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }

        setStatuses((current) => {
            const next = [...current];
            const moved = next.splice(result.source.index, 1)[0];

            if (!moved) {
                return current;
            }
            next.splice(result.destination?.index ?? result.source.index, 0, moved);
            return next;
        });
    };
    const rowStyle = (isDragging: boolean, style?: DraggableStyle) => ({
        alignItems: 'center',
        background: isDragging ? token.colorBgElevated : token.colorBgContainer,
        border: `1px dashed ${token.colorBorder}`,
        borderRadius: token.borderRadius,
        boxShadow: isDragging ? token.boxShadowSecondary : undefined,
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '0.5rem',
        padding: '0.5rem',
        ...style,
    });
    const handleSave = async () => {
        if (statuses.some((status) => !status.name.trim())) {
            toast.error('Every task status needs a name.');
            return;
        }

        try {
            setSaving(true);
            await taskService.statuses.saveConfiguration({
                statuses: statuses.map((status) => ({
                    category: status.category,
                    color: status.color,
                    id: status.id,
                    name: status.name.trim(),
                })),
            });
            onChanged();
            onClose();
            toast.success('Task Statuses Updated');
        } catch (error) {
            captureException(error);
            toast.error('Unable to Update Task Statuses');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            destroyOnHidden
            footer={
                <Flex align='center' justify='space-between'>
                    <LearnMoreFooter align='left' topic='Task statuses and workflow' />
                    <Flex gap='0.5rem'>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button loading={saving} onClick={() => void handleSave()} type='primary'>
                            Save Statuses
                        </Button>
                    </Flex>
                </Flex>
            }
            onCancel={onClose}
            open={open}
            styles={{
                body: { maxHeight: 'none', overflow: 'visible' },
                footer: {
                    borderTop: `1px solid ${token.colorBorder}`,
                    marginTop: '1rem',
                    paddingTop: '1rem',
                },
                header: {
                    borderBottom: `1px solid ${token.colorBorder}`,
                    marginBottom: '1rem',
                    paddingBottom: '1rem',
                },
            }}
            title={
                <Flex align='center' gap='0.5rem'>
                    <TbSettings />
                    Manage Task Statuses
                </Flex>
            }
            width='42rem'
        >
            <div
                style={{
                    border: `1px dashed ${token.colorBorder}`,
                    borderRadius: token.borderRadius,
                    minHeight: '18rem',
                    padding: '0.5rem',
                }}
            >
                {loading ? (
                    <div aria-label='Loading task statuses' style={{ minHeight: '17rem', padding: '0.75rem' }}>
                        <BaseSkeleton paragraph={{ rows: 7 }} title={false} />
                    </div>
                ) : statuses.length ? (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId='task-statuses'>
                            {(droppableProvided) => (
                                <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                                    {statuses.map((status, index) => (
                                        <Draggable draggableId={status.clientId} index={index} key={status.clientId}>
                                            {(draggableProvided, draggableSnapshot) => (
                                                <div
                                                    ref={draggableProvided.innerRef}
                                                    {...draggableProvided.draggableProps}
                                                    style={rowStyle(
                                                        draggableSnapshot.isDragging,
                                                        draggableProvided.draggableProps.style,
                                                    )}
                                                >
                                                    <DragHandle
                                                        dragHandleProps={draggableProvided.dragHandleProps}
                                                        isDragging={draggableSnapshot.isDragging}
                                                        label={`Reorder ${status.name}`}
                                                    />
                                                    <PlatformColorPicker
                                                        onChange={(color) =>
                                                            updateDraft(status.clientId, { color: color.toHexString() })
                                                        }
                                                        value={status.color}
                                                    >
                                                        <Button
                                                            aria-label={`Color for ${status.name}`}
                                                            icon={<TaskStatusIcon status={status} />}
                                                            size='small'
                                                            style={iconButtonStyle}
                                                        />
                                                    </PlatformColorPicker>
                                                    <Input
                                                        maxLength={80}
                                                        onChange={(event) =>
                                                            updateDraft(status.clientId, { name: event.target.value })
                                                        }
                                                        value={status.name}
                                                    />
                                                    <DropdownButtonSelect
                                                        disabled={status.isDefault}
                                                        footer={
                                                            <LearnMoreFooter
                                                                topic='task status categories and workflow'
                                                                variant='dropdown'
                                                            />
                                                        }
                                                        header={
                                                            <DropdownMenuLabel icon={<TbCategory2 size='0.875rem' />}>
                                                                Status Category
                                                            </DropdownMenuLabel>
                                                        }
                                                        matchTriggerWidth={false}
                                                        menuWidth='18rem'
                                                        options={categoryOptions}
                                                        placement='bottomRight'
                                                        value={status.category}
                                                        onChange={(category) =>
                                                            category && updateDraft(status.clientId, { category })
                                                        }
                                                    >
                                                        <DropdownButton
                                                            disabled={status.isDefault}
                                                            style={{
                                                                alignItems: 'center',
                                                                display: 'inline-flex',
                                                                justifyContent: 'space-between',
                                                                width: '8rem',
                                                            }}
                                                        >
                                                            <span>{status.category}</span>
                                                        </DropdownButton>
                                                    </DropdownButtonSelect>
                                                    <Button
                                                        aria-label={`Delete ${status.name}`}
                                                        danger
                                                        disabled={status.isDefault}
                                                        icon={<TbTrash />}
                                                        onClick={() => removeDraft(status)}
                                                        size='small'
                                                        style={iconButtonStyle}
                                                        title={
                                                            status.isDefault
                                                                ? 'Platform statuses can be renamed but not deleted.'
                                                                : 'Delete status'
                                                        }
                                                        type='text'
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {droppableProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    <EmptyView
                        description='Create a status to define how work progresses through your task workflow.'
                        extra={
                            <Button icon={<TbPlus />} onClick={addStatus} type='primary'>
                                Add Status
                            </Button>
                        }
                        height='17rem'
                        icon={<TbCategory2 size='1rem' />}
                        label='No Task Statuses'
                    />
                )}
                {!loading && statuses.length ? (
                    <Button block icon={<TbPlus />} onClick={addStatus} type='dashed'>
                        Add Status
                    </Button>
                ) : null}
            </div>
        </Modal>
    );
};

export default ManageTaskStatusesModal;
